/**
 * Client API helpers. All requests include cookies (`credentials: 'include'`)
 * so the httpOnly session cookie is sent on every call.
 */

/** Paladin carries two weapons; everyone else carries one in the right hand. */
export interface PaladinLoadout {
  right?: string;
  left?: string;
}

export type CosmeticLoadout = {
  warrior?: string;
  archer?: string;
  berserker?: string;
  crossbowman?: string;
  paladin?: PaladinLoadout;
  hero?: string;
};

export interface PublicUser {
  id: string;
  username: string;
  email: string;
  gamesPlayed: number;
  gamesWon: number;
  createdAt: string;
  /** Cosmetic currency — "Moustaches d'or". */
  shinies: number;
  /** Per-unit-type weapon selection. Missing entries fall back to defaults. */
  cosmeticLoadout: CosmeticLoadout;
  /** Weapon IDs the user has unlocked (in addition to free defaults). */
  cosmeticUnlocks: string[];
  /** ── Ranked-mode fields ─────────────────────────────────────── */
  rankedPoints: number;
  rankedGamesPlayed: number;
  rankedGamesWon: number;
  /** Default faction picked in the profile, auto-locked at ranked
   *  matchmaking time. Null = none picked yet (UI nags the user). */
  favoriteFactionId: string | null;
  /** True if this account has admin privileges. Drives the
   *  "Administration" nav link visibility + the /admin route guard. */
  isAdmin?: boolean;
}

export interface LeaderEntry {
  rank: number;
  id: string;
  username: string;
  gamesPlayed: number;
  gamesWon: number;
  isMe: boolean;
}

export interface LeaderboardResponse {
  top: LeaderEntry[];
  me: LeaderEntry | null;
  totalRanked: number;
}

/** Entry in the ranked leaderboard — extra fields vs the classic one. */
export interface RankedLeaderEntry {
  rank: number;
  id: string;
  username: string;
  rankedPoints: number;
  rankedGamesPlayed: number;
  rankedGamesWon: number;
  isMe: boolean;
}

export interface RankedLeaderboardResponse {
  top: RankedLeaderEntry[];
  me: RankedLeaderEntry | null;
  totalRanked: number;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let message = `Erreur ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // not JSON, ignore
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

export async function signup(input: {
  username: string;
  email: string;
  password: string;
}): Promise<PublicUser> {
  return request<PublicUser>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function login(identifier: string, password: string): Promise<PublicUser> {
  return request<PublicUser>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ identifier, password }),
  });
}

export async function logout(): Promise<void> {
  await request<{ ok: true }>('/auth/logout', { method: 'POST' });
}

export async function fetchMe(): Promise<PublicUser | null> {
  try {
    return await request<PublicUser>('/auth/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export async function fetchLeaderboard(): Promise<LeaderboardResponse> {
  return request<LeaderboardResponse>('/leaderboard');
}

export async function fetchRankedLeaderboard(): Promise<RankedLeaderboardResponse> {
  return request<RankedLeaderboardResponse>('/leaderboard/ranked');
}

// ─────────────────────────────────────────────────────────────────────────────
// Cosmetics
// ─────────────────────────────────────────────────────────────────────────────

export type UnitType =
  | 'warrior'
  | 'archer'
  | 'berserker'
  | 'crossbowman'
  | 'paladin'
  | 'hero';

export type WeaponCategory =
  | 'sword' | 'axe' | 'bat' | 'bow' | 'crossbow' | 'staff' | 'shield' | 'fun';

export interface WeaponDef {
  id: string;
  sprite: string;
  name: string;
  category: WeaponCategory;
  price: number;
  unitTypes: UnitType[];
  paladinLeft?: boolean;
}

export interface DefaultLoadout {
  warrior: string;
  archer: string;
  berserker: string;
  crossbowman: string;
  paladin: { right: string; left: string };
  hero: string;
}

export interface WeaponCatalog {
  weapons: WeaponDef[];
  categories: Array<{ id: WeaponCategory; label: string }>;
  unitLabels: Record<UnitType, string>;
  defaultLoadout: DefaultLoadout;
}

let weaponCatalogCache: WeaponCatalog | null = null;

/** Static — cached for the whole session after the first fetch. */
export async function fetchWeaponCatalog(): Promise<WeaponCatalog> {
  if (weaponCatalogCache) return weaponCatalogCache;
  const c = await request<WeaponCatalog>('/cosmetics/catalog');
  weaponCatalogCache = c;
  return c;
}

export async function unlockWeapon(weaponId: string): Promise<{
  shinies: number;
  cosmeticUnlocks: string[];
}> {
  return request('/cosmetics/unlock', {
    method: 'POST',
    body: JSON.stringify({ weaponId }),
  });
}

export async function saveLoadout(loadout: CosmeticLoadout): Promise<{
  cosmeticLoadout: CosmeticLoadout;
}> {
  return request('/cosmetics/loadout', {
    method: 'POST',
    body: JSON.stringify(loadout),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Ranked profile — favorite faction
// ─────────────────────────────────────────────────────────────────────────────

/** Set the user's favorite faction (auto-locked when queueing for ranked).
 *  Pass `null` to clear. */
export async function saveFavoriteFaction(factionId: string | null): Promise<{
  favoriteFactionId: string | null;
}> {
  return request('/profile/favorite-faction', {
    method: 'POST',
    body: JSON.stringify({ factionId }),
  });
}
